import React from "react";

import { ICellRendererParams } from "@ag-grid-community/core";
import Col from "antd/es/col";
import Row from "antd/es/row";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import Cadenced from "@mapmycustomers/shared/types/base/Cadenced";
import Groupable from "@mapmycustomers/shared/types/base/Groupable";
import { nameComparator } from "@mapmycustomers/shared/util/comparator";

import EntityBadge from "@app/component/EntityBadge";
import FrequencyInterval from "@app/component/frequency/FrequencyInterval";
import FrequencyEntity from "@app/component/frequency/type/FrequencyEntity";
import hasFrequency from "@app/component/frequency/util/hasFrequency";
import useRowHeightUpdater from "@app/component/grid/util/hooks/useRowHeightUpdater";
import layout from "@app/styles/layout";

import styles from "./FrequencyIntervalCellRenderer.module.scss";

const FrequencyIntervalCellRenderer: React.FC<ICellRendererParams> = ({ api, node, value }) => {
  useRowHeightUpdater(node, api);
  const intl = useIntl();

  if (!value) {
    // Wrapping into fragment because this component can't return a plain string.
    // On the other hand it can't return `null` because that's an AgGrid recommendation
    // to return empty strings: https://www.ag-grid.com/react-hooks/#react-null
    return <>{""}</>;
  }

  return (
    <div className={styles.container}>
      {hasFrequency(value as FrequencyEntity) && (
        <Tooltip
          title={intl.formatMessage(
            {
              id: "FrequencyInterval.frequencyForEntity",
              defaultMessage: "<b>Set just to this record</b>",
              description: "Frequency interval field - Set just to this record",
            },
            { b: (text) => <b>{text}</b> }
          )}
        >
          <span>
            <FrequencyInterval
              frequencyInterval={(value as Cadenced).cadenceInterval}
              isGroup={false}
            />
          </span>
        </Tooltip>
      )}
      {(value as Groupable).groups
        ?.filter(hasFrequency)
        .sort(nameComparator)
        .map((group) => (
          <Tooltip
            key={group.id}
            title={intl.formatMessage(
              {
                id: "FrequencyInterval.frequencyForGroup",
                defaultMessage: "<b>Set by Group</b>{br}{groupIcon}",
                description: "Frequency interval field - Set just to this record",
              },
              {
                b: (text) => <b>{text}</b>,
                br: <br />,
                groupIcon: (
                  <Row align="middle" gutter={layout.spacerXS}>
                    <Col>
                      <div className={styles.groupBadgeContainer}>
                        <EntityBadge className={styles.groupBadge} entity={group} hideName />
                      </div>
                    </Col>
                    <Col>{group.name}</Col>
                  </Row>
                ),
              }
            )}
          >
            <span>
              <FrequencyInterval
                frequencyInterval={group.cadenceInterval}
                groupColor={group.color}
                isGroup
              />
            </span>
          </Tooltip>
        ))}
    </div>
  );
};

export default FrequencyIntervalCellRenderer;
